/**
 * PinchRecognizer — two-finger zoom with elastic boundaries.
 *
 * Reports onPinch(scale, centerX, centerY) continuously.
 * Reports onPinchEnd() on release.
 * Elastic mode applies logarithmic resistance past min/max bounds.
 */

const DEFAULTS = {
    min: 1,
    max: 3,
    elastic: true,
};

export class PinchRecognizer {
    constructor(config, callbacks) {
        this.config = { ...DEFAULTS, ...config };
        this.callbacks = callbacks;
        this._active = false;
        this._initialDistance = 0;
        this._currentScale = 1;
    }

    start(pointer1, pointer2) {
        this._active = true;
        this._initialDistance = this._distance(pointer1, pointer2);
        if (this._initialDistance === 0) this._initialDistance = 1;
    }

    move(pointer1, pointer2) {
        if (!this._active) return;

        const currentDistance = this._distance(pointer1, pointer2);
        let rawScale = currentDistance / this._initialDistance;

        // Apply elastic resistance at boundaries
        if (this.config.elastic) {
            rawScale = this._applyElastic(rawScale);
        } else {
            rawScale = Math.max(this.config.min, Math.min(this.config.max, rawScale));
        }

        this._currentScale = rawScale;

        const cx = (pointer1.currentX + pointer2.currentX) / 2;
        const cy = (pointer1.currentY + pointer2.currentY) / 2;

        this.callbacks.onPinch?.(this._currentScale, cx, cy);
    }

    end() {
        if (!this._active) return;
        this._active = false;
        this.callbacks.onPinchEnd?.();
    }

    cancel() {
        if (!this._active) return;
        this._active = false;
        this.callbacks.onPinchEnd?.();
    }

    /**
     * Apply elastic resistance when scale exceeds bounds.
     * Formula: effectiveScale = limit + (raw - limit) / (1 + ln(1 + raw - limit))
     */
    _applyElastic(rawScale) {
        const { min, max } = this.config;

        if (rawScale > max) {
            const over = rawScale - max;
            return max + over / (1 + Math.log(1 + over));
        }

        if (rawScale < min) {
            const under = min - rawScale;
            return min - under / (1 + Math.log(1 + under));
        }

        return rawScale;
    }

    _distance(p1, p2) {
        const dx = p2.currentX - p1.currentX;
        const dy = p2.currentY - p1.currentY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /** Whether the current scale is outside the configured bounds. */
    isOutOfBounds() {
        return this._currentScale < this.config.min || this._currentScale > this.config.max;
    }

    /** Get the nearest bound for snap-back. */
    nearestBound() {
        if (this._currentScale < this.config.min) return this.config.min;
        if (this._currentScale > this.config.max) return this.config.max;
        return this._currentScale;
    }

    destroy() {
        this._active = false;
    }
}
